import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cpFetch, edgeFetchWithAuth } from '../../lib/cpClient';
import { getPollMs, isPageVisible } from '../../lib/perfMode';
import { useAuthStore } from '../../store/authStore';
import { getMailboxUnreadSummary, listAgentExecutions, listAppInstances } from '../apps/api';
import type { AppInstance, MailboxSummaryItem } from '../apps/types';
import { fetchFleetSummary } from '../iot/api';
import { presentServingTag } from '../../lib/serviceHealth';
import { useSharedServiceHealth } from '../../lib/ServiceHealthContext';
import {
  SERVICE_STATUS_QUERY_KEY,
  SERVICE_STATUS_STALE_MS,
  type ServicesStatusPayload,
} from '../../lib/serviceHealthQuery';

export {
  SERVICE_STATUS_QUERY_KEY,
  SERVICE_STATUS_STALE_MS,
};
export type { ServicesStatusPayload };

export type TelemetryHealthPayload = {
  freshnessSec: number;
  lastEventTs?: string;
};

export type TelemetryAggPayload = {
  msgsPerMin?: number;
  rate?: number;
};

export type AgenticNotification = {
  id: string;
  ts: string;
  title: string;
  detail: string;
  severity: 'info' | 'success' | 'warning' | 'error';
};

export function useHealthSummary(enabled = true) {
  const shared = useSharedServiceHealth();
  const payload = enabled ? shared.payload : null;

  const data = useMemo(() => {
    if (!payload) return null;
    const health = shared.health;

    return {
      ok: health.ok,
      total: health.total,
      degraded: health.degraded,
      down: health.down,
      status: health.status,
      variant: health.variant,
      label: health.label,
      overallOk: health.status === 'Healthy',
      updatedAt: payload?.updated_at || null,
      platform_build: presentServingTag(payload.platform_build),
    };
  }, [payload, shared.health]);

  return { ...shared.query, data };
}

export function useTelemetryHealth(enabled = true) {
  const { token, selectedTenant } = useAuthStore();

  return useQuery({
    queryKey: ['telemetry-health'],
    enabled: enabled && Boolean(token),
    queryFn: async () => {
      if (!token) throw new Error('No token');
      if (!isPageVisible()) return null;

      const response = await edgeFetchWithAuth('/cp/telemetry/health', token, selectedTenant || undefined);
      if (response.status === 503 || response.status === 502 || response.status === 504) {
        return { freshnessSec: Number.NaN } satisfies TelemetryHealthPayload;
      }
      if (!response.ok) throw new Error(`telemetry ${response.status}`);
      return await response.json() as TelemetryHealthPayload;
    },
    staleTime: 10_000,
    refetchInterval: getPollMs(30_000),
    retry: false,
  });
}

export function useTelemetryAgg(enabled = true) {
  const { token, selectedTenant } = useAuthStore();

  return useQuery({
    queryKey: ['telemetry-agg', '1h'],
    enabled: enabled && Boolean(token),
    queryFn: async () => {
      if (!token) throw new Error('No token');
      if (!isPageVisible()) return null;

      const response = await edgeFetchWithAuth('/cp/telemetry/agg?groupBy=tenant&window=1h', token, selectedTenant || undefined);
      if (response.status === 503 || response.status === 502 || response.status === 504) {
        return { msgsPerMin: undefined, rate: undefined } satisfies TelemetryAggPayload;
      }
      if (!response.ok) throw new Error(`agg ${response.status}`);
      return await response.json() as TelemetryAggPayload | TelemetryAggPayload[];
    },
    staleTime: 10_000,
    refetchInterval: getPollMs(30_000),
    retry: false,
  });
}

export function getTelemetryMsgsPerMin(
  payload: TelemetryAggPayload | TelemetryAggPayload[] | null | undefined
): number | undefined {
  const row = Array.isArray(payload) ? payload[0] : payload;
  if (!row) return undefined;
  const nested = (row as { summary?: { msgsPerMin?: number; rate?: number } }).summary;
  const value = row.msgsPerMin ?? row.rate ?? nested?.msgsPerMin ?? nested?.rate;
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

export function useAgenticNotifications(enabled = true) {
  return useQuery({
    queryKey: ['overview-agentic-notifications'],
    enabled,
    refetchInterval: getPollMs(15_000),
    queryFn: async () => {
      if (!isPageVisible()) return [];

      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const query = new URLSearchParams({
        stream: 'agent',
        limit: '80',
        since,
      });
      const data = await cpFetch(`/pulsar/events?${query.toString()}`) as { items?: any[] };
      const rows = Array.isArray(data?.items) ? data.items : [];

      return rows
        .map((row, index) => {
          const event = String(row?.event_name || row?.event_type || '').toLowerCase();
          const source = String(row?.source || '').toLowerCase();
          const requestId = row?.request_id ? `request ${row.request_id}` : 'unknown request';
          const id = String(row?._id || row?.id || `${row?.timestamp || 'unknown'}-${event}-${row?.request_id || index}`);

          if (event === 'triage_activated') {
            return {
              id,
              ts: row.timestamp,
              title: 'Triage activated',
              detail: `${requestId} moved to agentic triage`,
              severity: 'warning',
            } satisfies AgenticNotification;
          }

          if (event === 'self_heal_success') {
            return {
              id,
              ts: row.timestamp,
              title: 'Self-heal succeeded',
              detail: `${requestId} recovered automatically`,
              severity: 'success',
            } satisfies AgenticNotification;
          }

          if (event === 'self_heal_failed') {
            return {
              id,
              ts: row.timestamp,
              title: 'Self-heal escalated',
              detail: `${requestId} needs manual intervention`,
              severity: 'error',
            } satisfies AgenticNotification;
          }

          if (event === 'rollback_executed' || event === 'rollback_failed') {
            return {
              id,
              ts: row.timestamp,
              title: event === 'rollback_executed' ? 'Rollback executed' : 'Rollback failed',
              detail: `${requestId} rollback ${event === 'rollback_executed' ? 'completed' : 'failed'}`,
              severity: event === 'rollback_executed' ? 'info' : 'error',
            } satisfies AgenticNotification;
          }

          if (source.includes('pulsar_triage') && row?.error) {
            return {
              id,
              ts: row.timestamp,
              title: 'Triage error',
              detail: `${requestId}: ${String(row.error).slice(0, 120)}`,
              severity: 'error',
            } satisfies AgenticNotification;
          }

          return null;
        })
        .filter((item): item is AgenticNotification => Boolean(item))
        .sort((a, b) => +new Date(b.ts) - +new Date(a.ts))
        .slice(0, 8);
    },
  });
}

export function useAppInstances(enabled = true) {
  return useQuery({
    queryKey: ['overview-app-instances'],
    enabled,
    queryFn: async () => {
      const response = await listAppInstances();
      return Array.isArray(response?.apps) ? response.apps : [];
    },
    staleTime: 15_000,
    refetchInterval: getPollMs(30_000),
  });
}

export function useRecentAgentRuns(enabled = true, limit = 5) {
  return useQuery({
    queryKey: ['overview-agent-runs', limit],
    enabled,
    queryFn: async () => {
      const response = await listAgentExecutions({ limit });
      return Array.isArray(response?.executions) ? response.executions : [];
    },
    staleTime: 15_000,
    refetchInterval: getPollMs(30_000),
  });
}

export function useFleetSummary(enabled = true) {
  return useQuery({
    queryKey: ['overview-fleet-summary'],
    enabled,
    queryFn: async () => await fetchFleetSummary(),
    staleTime: 15_000,
    refetchInterval: getPollMs(30_000),
  });
}

function getMailboxSince(appId: string): string {
  const epochIso = new Date(0).toISOString();

  try {
    const raw = localStorage.getItem(`dc.mailbox_read.${appId}`);
    if (!raw) return epochIso;
    const parsed = new Date(raw);
    return Number.isFinite(parsed.getTime()) ? parsed.toISOString() : epochIso;
  } catch {
    return epochIso;
  }
}

export function useMailboxUnreadSummary(apps: AppInstance[], enabled = true) {
  const appIds = apps.map((app) => String(app?.app_id || '')).filter(Boolean);
  const sinceValues = useMemo(() => appIds.map((appId) => getMailboxSince(appId)), [appIds.join(',')]);

  return useQuery({
    queryKey: ['overview-mailbox-summary', appIds.join(','), sinceValues.join(',')],
    enabled: enabled && appIds.length > 0,
    queryFn: async () => {
      const response = await getMailboxUnreadSummary(appIds, sinceValues);
      return Array.isArray(response?.summary) ? response.summary : [];
    },
    staleTime: 10_000,
    refetchInterval: getPollMs(60_000),
  });
}

export function useMailboxUnreadMap(apps: AppInstance[], enabled = true) {
  const query = useMailboxUnreadSummary(apps, enabled);

  const unreadMap = useMemo(() => {
    const map = new Map<string, number>();

    for (const item of (query.data || []) as MailboxSummaryItem[]) {
      map.set(String(item?.app_id || ''), Number(item?.count || 0));
    }

    return map;
  }, [query.data]);

  return { ...query, unreadMap };
}
